<template>
  <div v-if="!hasPermission('List Cash Flow')">Você não tem permissão para acessar esta página!</div>
  <div v-else>
    <Toolbar/>
    <div class="container-xxl">
    <!-- Cards de Resumo -->
    <div class="row g-5 g-xl-8 mb-5">
      <div class="col-xl-4">
        <div class="card card-flush h-xl-100">
          <div class="card-header pt-5">
            <div class="card-title d-flex flex-column">
              <span class="card-label fw-bold text-gray-800">Saldo Atual</span>
              <span class="text-gray-400 mt-1 fw-semibold fs-6">Saldo acumulado no período</span>
            </div>
          </div>
          <div class="card-body pt-0 pb-5">
            <div class="d-flex flex-column text-center pt-8">
              <span :class="totals.balance >= 0 ? 'text-success' : 'text-danger'" 
                    class="fw-bold fs-2x mb-2">
                R$ {{ formatCurrency(totals.balance) }}
              </span>
              <span class="text-gray-500 fw-semibold fs-6">
                {{ formatDateRange() }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-4">
        <div class="card card-flush h-xl-100">
          <div class="card-header pt-5">
            <div class="card-title d-flex flex-column">
              <span class="card-label fw-bold text-gray-800">Total Recebido</span>
              <span class="text-gray-400 mt-1 fw-semibold fs-6">Entradas no período</span>
            </div>
          </div>
          <div class="card-body pt-0 pb-5">
            <div class="d-flex flex-column text-center pt-8">
              <span class="text-success fw-bold fs-2x mb-2">
                R$ {{ formatCurrency(totals.received) }}
              </span>
              <span class="text-gray-500 fw-semibold fs-6">
                {{ rows.length }} {{ rows.length === 1 ? 'dia' : 'dias' }} com recebimentos
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-4">
        <div class="card card-flush h-xl-100">
          <div class="card-header pt-5">
            <div class="card-title d-flex flex-column">
              <span class="card-label fw-bold text-gray-800">Total Pago</span>
              <span class="text-gray-400 mt-1 fw-semibold fs-6">Saídas no período</span>
            </div>
          </div>
          <div class="card-body pt-0 pb-5">
            <div class="d-flex flex-column text-center pt-8">
              <span class="text-danger fw-bold fs-2x mb-2">
                R$ {{ formatCurrency(totals.paid) }}
              </span>
              <span class="text-gray-500 fw-semibold fs-6">
                {{ rows.length }} {{ rows.length === 1 ? 'dia' : 'dias' }} com pagamentos
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Card Principal -->
    <div class="card">
      <div class="card-header border-0 pt-6">
        <div class="card-title">
          <h3 class="fw-bold m-0">Fluxo de Caixa</h3>
        </div>
        <div class="card-toolbar">
          <button class="btn btn-sm btn-light-primary" @click="exportData" v-if="rows.length > 0">
            <i class="bi bi-download"></i> Exportar
          </button>
        </div>
      </div>

      <div class="card-body pt-0">
        <!-- Filtros -->
        <div class="card mb-5 mb-xl-8">
          <div class="card-header border-0 pt-5">
            <h3 class="card-title align-items-start flex-column">
              <span class="card-label fw-bold fs-3 mb-1">Filtros</span>
            </h3>
          </div>
          <div class="card-body pt-0">
            <div class="row g-3">
              <div class="col-md-3">
                <label class="form-label">Data Inicial</label>
                <input type="date" v-model="filters.start_date" class="form-control">
              </div>
              <div class="col-md-3">
                <label class="form-label">Data Final</label>
                <input type="date" v-model="filters.end_date" class="form-control">
              </div>
              <div class="col-md-3">
                <label class="form-label">Conta Bancária</label>
                <select v-model="filters.bank_account_id" class="form-select">
                  <option value="">Todas as contas</option>
                  <option v-for="account in bankAccounts" :key="account.id" :value="account.id">
                    {{ account.name }}
                  </option>
                </select>
              </div>
              <div class="col-md-3 d-flex align-items-end gap-2">
                <button @click="fetchCashFlow" class="btn btn-primary flex-1">
                  <i class="bi bi-search"></i> Filtrar
                </button>
                <button @click="resetFilters" class="btn btn-light">
                  <i class="bi bi-arrow-clockwise"></i>
                </button>
              </div>
            </div>
            <div class="row g-3 mt-2">
              <div class="col-md-12">
                <div class="btn-group" role="group">
                  <button type="button" class="btn btn-sm" 
                          :class="periodFilter === 'today' ? 'btn-primary' : 'btn-light'"
                          @click="setPeriodFilter('today')">
                    Hoje
                  </button>
                  <button type="button" class="btn btn-sm"
                          :class="periodFilter === 'week' ? 'btn-primary' : 'btn-light'"
                          @click="setPeriodFilter('week')">
                    Esta Semana
                  </button>
                  <button type="button" class="btn btn-sm"
                          :class="periodFilter === 'month' ? 'btn-primary' : 'btn-light'"
                          @click="setPeriodFilter('month')">
                    Este Mês
                  </button>
                  <button type="button" class="btn btn-sm"
                          :class="periodFilter === 'year' ? 'btn-primary' : 'btn-light'"
                          @click="setPeriodFilter('year')">
                    Este Ano
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabela -->
        <div class="table-responsive">
          <table class="table table-row-bordered table-row-gray-300 align-middle gs-0 gy-4">
            <thead>
            <tr class="fw-bold text-muted">
              <th class="min-w-120px">Data</th>
              <th class="min-w-150px text-end">Total Recebido</th>
              <th class="min-w-150px text-end">Total Pago</th>
              <th class="min-w-150px text-end">Saldo do Dia</th>
              <th class="min-w-150px text-end">Saldo Acumulado</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(row, index) in rows" :key="row.date">
              <td>
                <span class="text-dark fw-bold d-block fs-6">{{ formatDate(row.date) }}</span>
                <span class="text-muted fw-semibold d-block fs-7">{{ formatDateWeekday(row.date) }}</span>
              </td>
              <td class="text-end">
                <span v-if="row.received > 0" class="text-success fw-bold d-block fs-6">
                  R$ {{ formatCurrency(row.received) }}
                </span>
                <span v-else class="text-muted fw-semibold d-block fs-7">-</span>
              </td>
              <td class="text-end">
                <span v-if="row.paid > 0" class="text-danger fw-bold d-block fs-6">
                  R$ {{ formatCurrency(row.paid) }}
                </span>
                <span v-else class="text-muted fw-semibold d-block fs-7">-</span>
              </td>
              <td class="text-end">
                <span :class="row.balance >= 0 ? 'text-success' : 'text-danger'" 
                      class="fw-bold d-block fs-6">
                  R$ {{ formatCurrency(row.balance) }}
                </span>
              </td>
              <td class="text-end">
                <span :class="getAccumulatedBalance(index) >= 0 ? 'text-success' : 'text-danger'" 
                      class="fw-bold d-block fs-6">
                  R$ {{ formatCurrency(getAccumulatedBalance(index)) }}
                </span>
              </td>
            </tr>
            <tr v-if="rows.length === 0">
              <td colspan="5" class="text-center text-muted py-10">
                <i class="bi bi-inbox fs-1"></i>
                <div class="mt-3">Nenhum dado encontrado para o período selecionado</div>
              </td>
            </tr>
            </tbody>
            <tfoot>
            <tr class="fw-bold bg-light">
              <td>TOTAL</td>
              <td class="text-end text-success">R$ {{ formatCurrency(totals.received) }}</td>
              <td class="text-end text-danger">R$ {{ formatCurrency(totals.paid) }}</td>
              <td class="text-end" :class="totals.balance >= 0 ? 'text-success' : 'text-danger'">
                R$ {{ formatCurrency(totals.balance) }}
              </td>
              <td class="text-end" :class="getAccumulatedBalance(rows.length - 1) >= 0 ? 'text-success' : 'text-danger'">
                R$ {{ formatCurrency(getAccumulatedBalance(rows.length - 1)) }}
              </td>
            </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import http from "@/helpers/http";
import Permission from "@/helpers/permission";
import Toolbar from "@/components/layouts/auth/Toolbar.vue";
import moment from "moment";

export default {
  name: "CashFlow",
  components: {Toolbar},
  data() {
    return {
      filters: {
        start_date: "",
        end_date: "",
        bank_account_id: ""
      },
      periodFilter: 'month',
      rows: [],
      totals: {
        received: 0,
        paid: 0,
        balance: 0
      },
      bankAccounts: [],
      initialBalance: 0
    };
  },
  methods: {
    fetchCashFlow() {
      http.get(`/places/${this.$store.state.auth.place.id_place}/finances/cash-flow`, {params: this.filters}).then(response => {
        this.rows = response.data.rows || [];
        this.initialBalance = response.data.initial_balance || 0;

        // Totalizações
        const totalReceived = this.rows.reduce((sum, r) => sum + (r.received || 0), 0);
        const totalPaid = this.rows.reduce((sum, r) => sum + (r.paid || 0), 0);

        this.totals = {
          received: totalReceived,
          paid: totalPaid,
          balance: totalReceived - totalPaid
        };
      }).catch(() => {
        this.rows = [];
        this.totals = { received: 0, paid: 0, balance: 0 };
      });
    },
    loadBankAccounts() {
      http.get(`/places/${this.$store.state.auth.place.id_place}/finances/bank-accounts`).then(response => {
        this.bankAccounts = response.data.filter(acc => acc.active !== false);
      }).catch(() => {
        this.bankAccounts = [];
      });
    },
    setPeriodFilter(period) {
      this.periodFilter = period;
      const today = moment();
      
      switch(period) {
        case 'today':
          this.filters.start_date = today.format('YYYY-MM-DD');
          this.filters.end_date = today.format('YYYY-MM-DD');
          break;
        case 'week':
          this.filters.start_date = today.clone().startOf('week').format('YYYY-MM-DD');
          this.filters.end_date = today.clone().endOf('week').format('YYYY-MM-DD');
          break;
        case 'month':
          this.filters.start_date = today.clone().startOf('month').format('YYYY-MM-DD');
          this.filters.end_date = today.clone().endOf('month').format('YYYY-MM-DD');
          break;
        case 'year':
          this.filters.start_date = today.clone().startOf('year').format('YYYY-MM-DD');
          this.filters.end_date = today.clone().endOf('year').format('YYYY-MM-DD');
          break;
      }
      
      this.fetchCashFlow();
    },
    resetFilters() {
      this.periodFilter = 'month';
      this.setPeriodFilter('month');
    },
    getAccumulatedBalance(index) {
      if (index < 0) return this.initialBalance;
      
      let accumulated = this.initialBalance;
      for (let i = 0; i <= index; i++) {
        accumulated += (this.rows[i].balance || 0);
      }
      return accumulated;
    },
    formatCurrency(value) {
      return parseFloat(value || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD/MM/YYYY');
    },
    formatDateWeekday(date) {
      if (!date) return '';
      return moment(date).format('dddd');
    },
    formatDateRange() {
      if (!this.filters.start_date || !this.filters.end_date) return '';
      const start = moment(this.filters.start_date).format('DD/MM/YYYY');
      const end = moment(this.filters.end_date).format('DD/MM/YYYY');
      return `${start} a ${end}`;
    },
    exportData() {
      // Criar CSV
      let csv = 'Data,Total Recebido,Total Pago,Saldo do Dia,Saldo Acumulado\n';
      
      this.rows.forEach((row, index) => {
        csv += `${this.formatDate(row.date)},`;
        csv += `${row.received || 0},`;
        csv += `${row.paid || 0},`;
        csv += `${row.balance || 0},`;
        csv += `${this.getAccumulatedBalance(index)}\n`;
      });
      
      // Adicionar totais
      csv += `\nTOTAL,${this.totals.received},${this.totals.paid},${this.totals.balance},${this.getAccumulatedBalance(this.rows.length - 1)}\n`;
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `fluxo-caixa-${moment().format('YYYY-MM-DD')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    hasPermission(permission) {
      return Permission.has(permission);
    }
  },
  mounted() {
    this.loadBankAccounts();
    const today = moment();
    this.filters.start_date = today.clone().startOf('month').format('YYYY-MM-DD');
    this.filters.end_date = today.clone().endOf('month').format('YYYY-MM-DD');
    this.fetchCashFlow();
  }
};
</script>

<style scoped>
.card-flush {
  border: 0;
  box-shadow: 0 0 20px 0 rgba(76, 87, 125, 0.1);
}
</style>
